import React, { FC, memo } from "react";
import { t } from "ttag";

import { RichText } from "../../../common/RichText";
import { IOptionCode } from "../../../models/catalogue";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { tabFeatureAttributeBlock } from "../elementIDs";
import {
    ITabFeatureAttributeBlock_AttributeTab,
    ITabFeatureAttributeBlock_ModelTab,
} from "../models";
import { setOptionValue, setSelectedRootProduct } from "../reducers";
import { rootProductsSelector } from "../selectors";
import { getOptionValueSet } from "../utils";

import styles from "./TabFeatureAttributeBlockModelTabContent.module.scss";

interface TabFeatureAttributeBlockModelTabContentProps {
    attribute: IOptionCode;
    attrIdx: number;
    attrTab: ITabFeatureAttributeBlock_AttributeTab;
    modelIdx: number;
    modelTab: ITabFeatureAttributeBlock_ModelTab;
    isSelected: boolean;
}

const areEqual = (
    prevProps: TabFeatureAttributeBlockModelTabContentProps,
    nextProps: TabFeatureAttributeBlockModelTabContentProps,
) => {
    // Compare all relevant props shallowly or by value
    return (
        prevProps.attribute === nextProps.attribute &&
        prevProps.attrIdx === nextProps.attrIdx &&
        prevProps.modelIdx === nextProps.modelIdx &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.attrTab.attribute_value ===
            nextProps.attrTab.attribute_value &&
        prevProps.modelTab.model === nextProps.modelTab.model &&
        prevProps.modelTab.content === nextProps.modelTab.content
    );
};

const TabFeatureAttributeBlockModelTabContentComponent: FC<
    TabFeatureAttributeBlockModelTabContentProps
> = ({ attribute, attrIdx, attrTab, modelIdx, modelTab, isSelected }) => {
    const dispatch = useAppDispatch();
    const allRootProducts = useAppSelector(
        (state) => state.configurator.entities.rootProducts,
    );
    const selectedRootProducts = useAppSelector((state) =>
        rootProductsSelector(state.configurator),
    );
    const optionValues = useAppSelector(
        (state) => state.configurator.ui.optionValues,
    );

    const selectedPIDs = new Set(selectedRootProducts.map((p) => p.id));
    const productMatches = selectedPIDs.has(modelTab.model);
    const selectedReduxValues = getOptionValueSet(optionValues[attribute]);
    const valueMatches = selectedReduxValues.has(attrTab.attribute_value);
    const isProductSelected = productMatches && valueMatches;

    const rootProduct = allRootProducts.find((rp) => rp.id === modelTab.model);

    const buttonText = isProductSelected ? t`Selected` : t`Select`;

    const onSelect = () => {
        if (!rootProduct) {
            console.error(
                `Cannot select root product ${modelTab.model} because it has not been loaded.`,
            );
            return;
        }
        dispatch(setSelectedRootProduct(rootProduct));
        dispatch(
            setOptionValue({
                namespace: rootProduct.product_class_slug,
                code: attribute,
                index: 0,
                totalNumValues: 0,
                value: attrTab.attribute_value,
            }),
        );
    };

    return (
        <article
            className={styles.root}
            id={tabFeatureAttributeBlock.modelTabContent(
                attrTab,
                attrIdx,
                modelTab,
                modelIdx,
            )}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={tabFeatureAttributeBlock.modelTabButton(
                attrTab,
                attrIdx,
                modelTab,
                modelIdx,
            )}
            hidden={!isSelected}
        >
            <div className={styles.text}>
                <RichText html={modelTab.content} />
                <button
                    className="button"
                    onClick={onSelect}
                    disabled={isProductSelected}
                >
                    {buttonText}
                </button>
            </div>
        </article>
    );
};

export const TabFeatureAttributeBlockModelTabContent = memo(
    TabFeatureAttributeBlockModelTabContentComponent,
    areEqual,
);
